/**
 * Verbatim statutory (or controlling case law) language for each state's
 * prescriptive-easement period, plus the office that records easements and
 * what recording actually costs.
 *
 * Every excerpt was transcribed from a FREE official source, a state
 * legislature or official code site, or a published opinion. No paid legal
 * database is used anywhere in this project.
 *
 * `excerpt: null` is deliberate and honest: it means the operative sentence
 * could not be verified from a free official source, so the page shows the
 * existing plain-English summary instead of a quotation we cannot stand
 * behind. Never fill one of these in from memory.
 */

export type StatuteExcerptKind = "statute" | "case-law";

export interface StateStatutoryExcerpt {
  /** USPS two-letter code. */
  stateCode: string;
  /** Verbatim operative language, or null when it could not be verified. */
  excerpt: string | null;
  /** Exact section or case citation the excerpt comes from. */
  sourceSection: string;
  kind: StatuteExcerptKind;
  /** Free official source for the quoted text. */
  sourceUrl: string;
  /** ISO date the text was last checked. */
  checkedOn: string;
  /** Official title of the county office that records easements. */
  recordingOffice: string;
  /** What recording costs, and whether the state sets it by statute. */
  recordingFeeNote: string;
  /** Source for the fee figure. */
  recordingFeeUrl: string;
}

export const STATE_STATUTORY_EXCERPTS: StateStatutoryExcerpt[] = [
  {
    stateCode: "IL",
    excerpt:
      "No person shall commence an action for the recovery of lands, nor make an entry thereon, unless within 20 years after the right to bring such action or make such entry first accrued...",
    sourceSection: "735 ILCS 5/13-101",
    kind: "statute",
    sourceUrl:
      "https://ilga.gov/documents/legislation/ilcs/documents/073500050K13-101.htm",
    checkedOn: "2026-09-07",
    recordingOffice: "County Recorder (Recorder of Deeds)",
    recordingFeeNote:
      "Recording fees are set by statute and vary by county class: a deed is generally not less than $31 in first-class counties and not less than $39 elsewhere, including the state Rental Housing Support surcharge.",
    recordingFeeUrl:
      "https://www.ilga.gov/documents/legislation/ilcs/documents/005500050K3-5018.2.htm",
  },
  {
    stateCode: "IN",
    excerpt: null,
    sourceSection: "Ind. Code § 32-23-1-1 et seq. (Easements: By Prescription)",
    kind: "statute",
    sourceUrl: "https://iga.in.gov/laws/2024/ic/titles/32#32-23-1",
    checkedOn: "2026-09-07",
    recordingOffice: "County Recorder",
    recordingFeeNote:
      "Set by statute at Ind. Code § 36-2-7-10: $25 to record a deed or other instrument, $55 for a mortgage, plus small state surcharges.",
    recordingFeeUrl: "https://iga.in.gov/laws/2024/ic/titles/36#36-2-7-10",
  },
  {
    stateCode: "IA",
    excerpt:
      "In all actions hereafter brought, in which title to any easement in real estate shall be claimed by virtue of adverse possession thereof for the period of ten years, the use of the same shall not be admitted as evidence that the party claimed the easement as the party's right...",
    sourceSection: "Iowa Code § 564.1",
    kind: "statute",
    sourceUrl: "https://www.legis.iowa.gov/docs/code/564.pdf",
    checkedOn: "2026-09-07",
    recordingOffice: "County Recorder",
    recordingFeeNote:
      "Set by statute at Iowa Code § 331.604: $5.00 for each page or part page recorded, plus a $1.00 records-management fee per transaction. Iowa charges per page rather than a flat first-page rate.",
    recordingFeeUrl: "https://www.legis.iowa.gov/docs/code/331.604.pdf",
  },
  {
    stateCode: "KS",
    excerpt:
      "No action shall be maintained against any person for the recovery of real property who has been in open, exclusive and continuous possession of such real property, either under a claim knowingly adverse or under a belief of ownership, for a period of fifteen (15) years.",
    sourceSection: "K.S.A. 60-503",
    kind: "statute",
    sourceUrl: "https://ksrevisor.gov/statutes/chapters/ch60/060_005_0003.html",
    checkedOn: "2026-09-07",
    recordingOffice: "Register of Deeds",
    recordingFeeNote:
      "Set by statute at K.S.A. 28-115, which carries the current per-page schedule for deeds and easements. Check the section text for the amount in force before you budget.",
    recordingFeeUrl: "https://ksrevisor.gov/statutes/chapters/ch28/028_001_0015.html",
  },
  {
    stateCode: "KY",
    excerpt:
      "An action for the recovery of real property may be brought only within fifteen (15) years after the right to institute it first accrued to the plaintiff, or to the person through whom he claims.",
    sourceSection: "KRS 413.010",
    kind: "statute",
    sourceUrl: "https://apps.legislature.ky.gov/law/statutes/statute.aspx?id=17853",
    checkedOn: "2026-09-07",
    recordingOffice: "County Clerk",
    recordingFeeNote:
      "Set by statute at KRS 64.012: $12.00 to record and index a deed of up to three pages, $3.00 for each additional page, and $4.00 for each extra reference to the same instrument.",
    recordingFeeUrl: "https://apps.legislature.ky.gov/law/statutes/statute.aspx?id=57720",
  },
  {
    stateCode: "LA",
    excerpt: null,
    sourceSection: "La. Civ. Code art. 742 (acquisitive prescription of servitudes)",
    kind: "statute",
    sourceUrl: "https://www.legis.la.gov/legis/Law.aspx?d=109457",
    checkedOn: "2026-09-07",
    recordingOffice:
      "Clerk of Court, as ex officio recorder, in Orleans Parish, the Register of Conveyances",
    recordingFeeNote:
      "Set by statute at La. R.S. 13:844 on a tiered page-count basis rather than a per-page rate: broadly $100 for a document of 1–5 pages, more for longer instruments.",
    recordingFeeUrl: "https://www.legis.la.gov/legis/Law.aspx?d=76185",
  },
  {
    stateCode: "ME",
    excerpt:
      "No person, class of persons or the public shall acquire a right-of-way or other easement through, in, upon or over the land of another by the adverse use and enjoyment thereof, unless it is continued uninterruptedly for 20 years.",
    sourceSection: "14 M.R.S. § 812",
    kind: "statute",
    sourceUrl: "https://www.mainelegislature.org/legis/statutes/14/title14sec812.html",
    checkedOn: "2026-09-07",
    recordingOffice: "Register of Deeds (county Registry of Deeds)",
    recordingFeeNote:
      "Set by statute at 33 M.R.S. § 751: $19 for the first recorded page and $2 for each additional page.",
    recordingFeeUrl: "https://www.mainelegislature.org/legis/statutes/33/title33sec751.html",
  },
  {
    stateCode: "MD",
    excerpt:
      "It is a familiar principle that to establish a right of way by prescription it is necessary to prove an adverse, exclusive and uninterrupted use of the way for twenty years.",
    sourceSection: "Garrett v. Gray, 258 Md. 363 (1970)",
    kind: "case-law",
    sourceUrl: "https://openjurist.org/258/md/363/garrett-v-gray-2178728",
    checkedOn: "2026-09-07",
    recordingOffice: "Clerk of the Circuit Court, Land Records Department",
    recordingFeeNote:
      "Recording fees are set by statute at Md. Real Property § 3-601 and carry a state surcharge on top of a page-count-based fee. Confirm the current amount with the clerk before filing.",
    recordingFeeUrl:
      "https://mgaleg.maryland.gov/mgawebsite/Laws/StatuteText?article=grp&section=3-601",
  },
  {
    stateCode: "MA",
    excerpt:
      "No person shall acquire by adverse use or enjoyment a right or privilege of way or other easement from, in, upon or over the land of another, unless such use or enjoyment is continued uninterruptedly for twenty years.",
    sourceSection: "M.G.L. c. 187, § 2",
    kind: "statute",
    sourceUrl:
      "https://malegislature.gov/Laws/GeneralLaws/PartII/TitleI/Chapter187/Section2",
    checkedOn: "2026-09-07",
    recordingOffice: "Register of Deeds (county Registry of Deeds)",
    recordingFeeNote:
      "Massachusetts uses a flat statewide Registry of Deeds schedule rather than a per-page rate; the published schedule lists a deed at $155.",
    recordingFeeUrl: "https://www.sec.state.ma.us/divisions/registry-of-deeds/rod-fees.htm",
  },
  {
    stateCode: "MI",
    excerpt:
      "No person may bring or maintain any action for the recovery or possession of any lands or make any entry upon any lands unless, after the claim or right to make the entry first accrued...",
    sourceSection:
      "MCL 600.5801. Applied to prescriptive easements in Marlette Auto Wash v. Van Dyke SC Props., 501 Mich. 192 (2018)",
    kind: "statute",
    sourceUrl: "https://www.legislature.mi.gov/Laws/MCL?objectName=MCL-600-5801",
    checkedOn: "2026-09-07",
    recordingOffice: "Register of Deeds",
    recordingFeeNote:
      "Set by statute at MCL 600.2567(1)(a): a flat $30.00 to enter and record a document, whatever its page count.",
    recordingFeeUrl: "https://legislature.mi.gov/Laws/MCL?objectName=mcl-600-2567",
  },
  {
    stateCode: "MN",
    excerpt:
      "No action for the recovery of real estate or the possession thereof shall be maintained unless it appears that the plaintiff, the plaintiff's ancestor, predecessor, or grantor was seized or possessed of the premises in question within 15 years before the beginning of the action.",
    sourceSection: "Minn. Stat. § 541.02",
    kind: "statute",
    sourceUrl: "https://www.revisor.mn.gov/statutes/cite/541.02/pdf",
    checkedOn: "2026-09-07",
    recordingOffice: "County Recorder",
    recordingFeeNote:
      "Set by statute at Minn. Stat. § 357.18, subd. 1(1): a flat $46 to index and record a deed or other instrument.",
    recordingFeeUrl: "https://www.revisor.mn.gov/statutes/cite/357.18/pdf",
  },
  {
    stateCode: "MS",
    excerpt: null,
    sourceSection: "Miss. Code Ann. § 15-1-13 (ten years' adverse possession gives title)",
    kind: "statute",
    sourceUrl: "https://law.justia.com/codes/mississippi/title-15/chapter-1/section-15-1-13/",
    checkedOn: "2026-09-07",
    recordingOffice: "Chancery Clerk",
    recordingFeeNote:
      "Set by statute at Miss. Code Ann. § 25-7-9: $26.00 to record a deed, which covers indexing for the first five pages, with a per-page charge beyond that.",
    recordingFeeUrl: "https://law.justia.com/codes/mississippi/title-25/chapter-7/section-25-7-9/",
  },
  {
    stateCode: "MO",
    excerpt: null,
    sourceSection:
      "RSMo § 516.010, the ten-year statute for actions to recover land, applied to prescriptive easements by Missouri courts",
    kind: "statute",
    sourceUrl: "https://revisor.mo.gov/main/OneSection.aspx?section=516.010",
    checkedOn: "2026-09-07",
    recordingOffice: "Recorder of Deeds",
    recordingFeeNote:
      "Set by statute at RSMo § 59.310; in non-charter counties the published rate is $24.00 for the first page and $3.00 per additional page. Charter counties may set their own by ordinance.",
    recordingFeeUrl: "https://revisor.mo.gov/main/OneSection.aspx?section=59.310",
  },
  {
    stateCode: "AL",
    excerpt:
      "An easement by prescription is acquired by use of the premises over which the easement is claimed for a period of twenty years or more, adversely to the owner...",
    sourceSection: "Jones v. Johnson, 827 So. 2d 768 (Ala. 2002)",
    kind: "case-law",
    sourceUrl: "https://hallapproved.com/al/cases/supreme/2002/1824071/",
    checkedOn: "2026-09-07",
    recordingOffice: "Judge of Probate (county Probate Office, Recording Department)",
    recordingFeeNote:
      "Base recording fees come from Ala. Code § 12-19-90, but each probate office adds technology and preservation charges, so a standard deed usually runs about $30–$36 for the first page plus per-page charges.",
    recordingFeeUrl: "https://www.tuscco.com/probate/records-and-recordings/",
  },
  {
    stateCode: "AK",
    excerpt:
      "Except as provided in (b) of this section, a person may not bring an action for the recovery of real property or for the recovery of the possession of it unless the action is commenced within 10 years.",
    sourceSection: "AS 09.10.030(a)",
    kind: "statute",
    sourceUrl:
      "https://touchngo.com/lglcntr/akstats/Statutes/Title09/Chapter10/Section030.htm",
    checkedOn: "2026-09-07",
    recordingOffice:
      "District Recorder, Alaska Department of Natural Resources. Alaska has no counties, so recording runs through state recording districts",
    recordingFeeNote:
      "Recording fees are set statewide by the Department of Natural Resources rather than by county, and the current schedule is published by the Recorder's Office.",
    recordingFeeUrl: "https://dnr.alaska.gov/ssd/recoff/",
  },
  {
    stateCode: "AZ",
    excerpt:
      "A person who has a cause of action for recovery of any lands, tenements or hereditaments from a person having peaceable and adverse possession thereof, cultivating, using or enjoying the same, shall commence an action ... within ten years after the cause of action accrues, and not afterward.",
    sourceSection: "A.R.S. § 12-526(A)",
    kind: "statute",
    sourceUrl: "https://www.azleg.gov/ars/12/00526.htm",
    checkedOn: "2026-09-07",
    recordingOffice: "County Recorder",
    recordingFeeNote:
      "Set by statute at A.R.S. § 11-475: a flat $30 per instrument, whatever its page count. Plats and surveys are priced separately under § 11-481.",
    recordingFeeUrl: "https://www.azleg.gov/ars/11/00475.htm",
  },
  {
    stateCode: "AR",
    excerpt: null,
    sourceSection:
      "No statute sets Arkansas's prescriptive-easement period; courts apply seven years (Gazaway v. Pugh, 69 Ark. App. 297, 12 S.W.3d 662 (2000)). Related statutes: Ark. Code Ann. § 18-61-101 (seven-year limit on actions to recover land) and § 18-11-106 (adverse possession of title with color of title and tax payment)",
    kind: "case-law",
    sourceUrl: "https://www.arkleg.state.ar.us/ArkansasLaw",
    checkedOn: "2026-09-07",
    recordingOffice: "Circuit Clerk (county recorder)",
    recordingFeeNote:
      "Recording fees are set statewide by statute at Ark. Code Ann. § 21-6-306; confirm the current first-page and additional-page amounts against that section.",
    recordingFeeUrl: "https://www.arkleg.state.ar.us/ArkansasLaw",
  },
  {
    stateCode: "CA",
    excerpt:
      "Occupancy for the period prescribed by the Code of Civil Procedure as sufficient to bar any action for the recovery of the property confers a title thereto, denominated a title by prescription, which is sufficient against all...",
    sourceSection: "Cal. Civ. Code § 1007 (period set by Civ. Proc. Code §§ 321, 325. Five years)",
    kind: "statute",
    sourceUrl: "https://leginfo.legislature.ca.gov/faces/codes.xhtml",
    checkedOn: "2026-09-07",
    recordingOffice: "County Recorder",
    recordingFeeNote:
      "Base fees are statutory, roughly $10 for the first page and $3 per additional page under Gov. Code § 27361, but statewide surcharges such as the Building Homes and Jobs Act fee push the real cost well above that, and every county publishes its own combined schedule.",
    recordingFeeUrl: "https://rrcc.lacounty.gov/GENERAL/Schedule_Fees.cfm",
  },
  {
    stateCode: "CO",
    excerpt:
      "No person shall commence or maintain an action for the recovery of the title or possession or to enforce or establish any right or interest of or to real property or make an entry thereon unless commenced within eighteen years after the right to bring such action or make such entry has first accrued...",
    sourceSection: "C.R.S. § 38-41-101(1)",
    kind: "statute",
    sourceUrl:
      "https://law.justia.com/codes/colorado/title-38/real-property/limitations-homestead-exemptions/article-41/part-1/section-38-41-101/",
    checkedOn: "2026-09-07",
    recordingOffice: "County Clerk and Recorder",
    recordingFeeNote:
      "Set by statute at C.R.S. § 30-1-103(1): $10 for the first page and $5 for each additional page where no other fee is specified.",
    recordingFeeUrl:
      "https://law.justia.com/codes/colorado/title-30/compensation-fees/article-1/section-30-1-103/",
  },
  {
    stateCode: "CT",
    excerpt: null,
    sourceSection: "Conn. Gen. Stat. § 47-37 (\u201cWhen acquired by adverse use\u201d). Fifteen years",
    kind: "statute",
    sourceUrl: "https://cga.ct.gov/current/PUB/chap_822.htm",
    checkedOn: "2026-09-07",
    recordingOffice: "Town Clerk",
    recordingFeeNote:
      "Set by statute at Conn. Gen. Stat. § 7-34a. The long-standing base was $10 for the first page and $5 per additional page; recent amendments raised the first-page charge for land records substantially, so check the current section text.",
    recordingFeeUrl: "https://www.cga.ct.gov/current/pub/chap_092.htm",
  },
  {
    stateCode: "DE",
    excerpt:
      "No person shall make an entry into any lands, tenements, or hereditaments, but within 20 years next after the person's right or title to the same first descended or accrued.",
    sourceSection: "10 Del. C. § 7901",
    kind: "statute",
    sourceUrl: "https://delcode.delaware.gov/title10/c079/",
    checkedOn: "2026-09-07",
    recordingOffice: "Recorder of Deeds",
    recordingFeeNote:
      "A statutory base fee plus county surcharges: county schedules show roughly a $36 document fee (state, county and technology components) plus about $10 per page including the first.",
    recordingFeeUrl:
      "https://www.kentcountyde.gov/files/sharedassets/public/v/2/content-publishers/deeds/deeds-pdf/recorder-of-deeds-schedule-of-fees-new-2024.pdf",
  },
  {
    stateCode: "DC",
    excerpt: null,
    sourceSection:
      "D.C. common law applying the fifteen-year limitations period of D.C. Code § 12-301 to prescriptive easements, see Smith v. Tippett, 569 A.2d 1186 (D.C. 1990)",
    kind: "case-law",
    sourceUrl: "https://code.dccouncil.gov/us/dc/council/code/sections/12-301",
    checkedOn: "2026-09-07",
    recordingOffice: "Recorder of Deeds, D.C. Office of Tax and Revenue",
    recordingFeeNote:
      "The Recorder of Deeds publishes the operative schedule: $150 to record a deed of trust or mortgage and $25 for most other recordable documents, including deeds, plus a surcharge.",
    recordingFeeUrl: "https://otr.cfo.dc.gov/service/general-recording-requirements-fees",
  },
  {
    stateCode: "FL",
    excerpt: null,
    sourceSection:
      "Florida common law, the twenty-year prescriptive period stated in Downing v. Bird, 100 So. 2d 57 (Fla. 1958). Fla. Stat. § 95.361 covers only presumed dedication of maintained public roads.",
    kind: "case-law",
    sourceUrl:
      "https://www.leg.state.fl.us/Statutes/index.cfm?App_mode=Display_Statute&URL=0000-0099/0095/Sections/0095.361.html",
    checkedOn: "2026-09-07",
    recordingOffice: "Clerk of the Circuit Court, acting as county recorder",
    recordingFeeNote:
      "Capped by statute at Fla. Stat. § 28.24: generally $10 for the first page and $8.50 for each additional page.",
    recordingFeeUrl:
      "https://www.leg.state.fl.us/Statutes/index.cfm?App_mode=Display_Statute&URL=0000-0099/0028/Sections/0028.24.html",
  },
  {
    stateCode: "GA",
    excerpt:
      "Possession of real property in conformance with the requirements of Code Section 44-5-161 for a period of 20 years shall confer good title by prescription to the property against everyone except the state and those persons laboring under the disabilities stated in Code Section 44-5-170.",
    sourceSection: "O.C.G.A. § 44-5-163",
    kind: "statute",
    sourceUrl:
      "https://law.justia.com/codes/georgia/title-44/chapter-5/article-7/section-44-5-163/",
    checkedOn: "2026-09-07",
    recordingOffice: "Clerk of the Superior Court",
    recordingFeeNote:
      "Set by statute at O.C.G.A. § 15-6-77, as amended in 2020: most real-estate documents, deeds included, cost a flat $25 to record regardless of page count.",
    recordingFeeUrl: "https://law.justia.com/codes/georgia/title-15/chapter-6/article-3/section-15-6-77/",
  },
  {
    stateCode: "HI",
    excerpt:
      "No person shall commence an action to recover possession of any lands, or make any entry thereon, unless within twenty years after the right to bring the action first accrued.",
    sourceSection: "Haw. Rev. Stat. § 657-31",
    kind: "statute",
    sourceUrl:
      "https://www.capitol.hawaii.gov/hrscurrent/Vol13_Ch0601-0676/HRS0657/HRS_0657-0031.htm",
    checkedOn: "2026-09-07",
    recordingOffice:
      "Registrar, State Bureau of Conveyances: Hawaii records centrally, not by county",
    recordingFeeNote:
      "A flat per-document fee set by the Bureau of Conveyances: about $36 for a document up to 50 pages and $101 for longer instruments.",
    recordingFeeUrl: "https://dlnr.hawaii.gov/boc/recording-fees-new/",
  },
  {
    stateCode: "ID",
    excerpt:
      "No action for the recovery of real property, or for the recovery of the possession thereof, can be maintained, unless it appears that the plaintiff, his ancestor, predecessor or grantor, was seized or possessed of the property in question within twenty (20) years before the commencement of the action...",
    sourceSection: "Idaho Code § 5-203",
    kind: "statute",
    sourceUrl: "https://legislature.idaho.gov/statutesrules/idstat/title5/t5ch2/sect5-203/",
    checkedOn: "2026-09-07",
    recordingOffice: "County Recorder",
    recordingFeeNote:
      "Set by statute at Idaho Code § 31-3205: generally $10 for the first page and $3 per additional page, with a flat $15 for deeds and conveyances of 30 pages or fewer.",
    recordingFeeUrl:
      "https://legislature.idaho.gov/statutesrules/idstat/title31/t31ch32/sect31-3205/",
  },
  {
    stateCode: "RI",
    excerpt:
      "Where any person or persons, or others from whom he, she, or they derive their title, either by themselves, tenants or lessees, shall have been for the space of ten (10) years in the uninterrupted, quiet, peaceful and actual seisin and possession of any lands, tenements or hereditaments...",
    sourceSection: "R.I. Gen. Laws § 34-7-1",
    kind: "statute",
    sourceUrl: "https://webserver.rilegislature.gov/Statutes/TITLE34/34-7/34-7-1.htm",
    checkedOn: "2026-09-07",
    recordingOffice: "Town or City Clerk, as recorder of deeds",
    recordingFeeNote:
      "Rhode Island records at the municipal level under R.I. Gen. Laws Title 34, Chapter 13; the charge for a land-record document commonly runs from roughly $75 upward depending on the municipality and instrument.",
    recordingFeeUrl: "https://webserver.rilegislature.gov/Statutes/TITLE34/34-13/34-13-1.htm",
  },
  {
    stateCode: "SC",
    excerpt:
      "No action for the recovery of real property or for the recovery of the possession of real property may be maintained unless it appears that the plaintiff, his ancestor, predecessor, or grantor, was seized or possessed of the premises in question within ten years before the commencement of the action.",
    sourceSection:
      "S.C. Code Ann. § 15-3-340; South Carolina courts also apply a common-law twenty-year prescriptive presumption",
    kind: "statute",
    sourceUrl: "https://www.scstatehouse.gov/code/t15c003.php",
    checkedOn: "2026-09-07",
    recordingOffice:
      "Register of Deeds, or the Clerk of Court in counties without a separate register",
    recordingFeeNote:
      "Two charges apply: the deed recording fee of $1.85 per $500 of value under S.C. Code § 12-24-10, plus the flat filing charge set by the Predictable Recording Fee Act, § 8-21-310.",
    recordingFeeUrl: "https://dor.sc.gov/tax-index/deed-recording-fee",
  },
  {
    stateCode: "SD",
    excerpt: null,
    sourceSection:
      "S.D. Codified Laws §§ 15-3-1 and 15-3-2 (twenty years' seizin or possession required to bring an action to recover real property)",
    kind: "statute",
    sourceUrl: "https://sdlegislature.gov/Statutes/15-3-1",
    checkedOn: "2026-09-07",
    recordingOffice: "Register of Deeds",
    recordingFeeNote:
      "Set by statute at S.D. Codified Laws § 7-9-15: $30 for the first page of a deed, mortgage or other instrument, with the schedule continuing per additional page.",
    recordingFeeUrl: "https://sdlegislature.gov/Statutes/7-9-15",
  },
  {
    stateCode: "TN",
    excerpt: null,
    sourceSection:
      "Tenn. Code Ann. § 28-2-101 (seven-year adverse possession under assurance of title), read with Tennessee's common-law twenty-year prescriptive-easement rule",
    kind: "case-law",
    sourceUrl: "https://www.tn.gov/sos/acts.html",
    checkedOn: "2026-09-07",
    recordingOffice: "Register of Deeds",
    recordingFeeNote:
      "Set by statute at Tenn. Code Ann. § 8-21-1001, plus a $2 data-processing charge per instrument; registers publish the resulting per-page amounts.",
    recordingFeeUrl:
      "https://www.nashville.gov/departments/register-deeds/recording-services/filing-fees",
  },
  {
    stateCode: "TX",
    excerpt:
      "\u201cAdverse possession\u201d means an actual and visible appropriation of real property, commenced and continued under a claim of right that is inconsistent with and is hostile to the claim of another person.",
    sourceSection:
      "Tex. Civ. Prac. & Rem. Code § 16.021, applied with the ten-year period of § 16.026; Texas courts use that period for prescriptive easements (Othen v. Rosier, 226 S.W.2d 622 (Tex. 1950))",
    kind: "statute",
    sourceUrl: "https://statutes.capitol.texas.gov/Docs/CP/htm/CP.16.htm",
    checkedOn: "2026-09-07",
    recordingOffice: "County Clerk",
    recordingFeeNote:
      "Set by statute at Tex. Local Gov't Code §§ 118.011 and 118.013, which fix the real-property filing fee for the first page and each additional page.",
    recordingFeeUrl: "https://statutes.capitol.texas.gov/docs/lg/pdf/lg.118.pdf",
  },
  {
    stateCode: "UT",
    excerpt:
      "Adverse possession may not be established unless it is shown that the land has been occupied and claimed continuously for seven years, and that the party and the party's predecessors and grantors have paid all taxes which have been levied and assessed upon the land according to law.",
    sourceSection:
      "Utah Code § 78B-2-214; Utah courts apply the seven-year period, without the tax element, to prescriptive easements (Judd v. Bowen, 2018 UT 47)",
    kind: "statute",
    sourceUrl: "https://le.utah.gov/xcode/Title78B/Chapter2/78B-2-S214.html",
    checkedOn: "2026-09-07",
    recordingOffice: "County Recorder",
    recordingFeeNote:
      "Fees are prescribed under Utah Code § 17-21-18; county schedules commonly show a flat charge of about $45 per document.",
    recordingFeeUrl: "https://le.utah.gov/xcode/Title17/Chapter21/17-21-S18.html",
  },
  {
    stateCode: "VT",
    excerpt:
      "Except as otherwise provided in 32 V.S.A. § 5263, an action for the recovery of lands, or the possession thereof, shall not be maintained, unless commenced within 15 years after the cause of action first accrued...",
    sourceSection: "12 V.S.A. § 501",
    kind: "statute",
    sourceUrl: "https://legislature.vermont.gov/statutes/section/12/023/00501",
    checkedOn: "2026-09-07",
    recordingOffice: "Town Clerk",
    recordingFeeNote:
      "Set by statute at 32 V.S.A. § 1671, which fixes town clerk recording fees on a per-page basis.",
    recordingFeeUrl: "https://legislature.vermont.gov/statutes/section/32/017/01671",
  },
  {
    stateCode: "VA",
    excerpt:
      "(i) adverse; (ii) under a claim of right; (iii) exclusive; (iv) continuous and uninterrupted; and (v) with the knowledge and acquiescence of the owners of the land over which it passes.",
    sourceSection:
      "Pettus v. Keeling, 232 Va. 483 (1987), the elements a prescriptive claim must prove over Virginia's twenty-year period",
    kind: "case-law",
    sourceUrl: "https://openjurist.org/232/va/483/pettus-v-keeling-1340569",
    checkedOn: "2026-09-07",
    recordingOffice: "Clerk of the Circuit Court",
    recordingFeeNote:
      "Set by statute at Va. Code § 17.1-275, which fixes the clerk's recording and indexing fees by page count, on top of applicable recordation taxes.",
    recordingFeeUrl: "https://law.lis.virginia.gov/vacode/section/17.1-275/",
  },
  {
    stateCode: "WA",
    excerpt: null,
    sourceSection:
      "Washington's prescriptive-easement rule is judge-made and borrows the ten-year period of RCW 4.16.020 (Dunbar v. Heinrich, 95 Wn.2d 20 (1980))",
    kind: "case-law",
    sourceUrl: "https://app.leg.wa.gov/rcw/default.aspx?cite=4.16.020",
    checkedOn: "2026-09-07",
    recordingOffice: "County Auditor, as recording officer",
    recordingFeeNote:
      "Set by statute at RCW 36.18.010: $5 for the first page and $1 for each additional page of a recorded instrument, before statutory surcharges.",
    recordingFeeUrl: "https://apps.leg.wa.gov/rcw/default.aspx?cite=36.18.010",
  },
  {
    stateCode: "WV",
    excerpt: null,
    sourceSection:
      "West Virginia applies a common-law ten-year prescriptive period borrowed from W. Va. Code § 55-2-1 (O'Dell v. Stegall, 703 S.E.2d 561 (W. Va. 2010))",
    kind: "case-law",
    sourceUrl: "https://code.wvlegislature.gov/55-2-1/",
    checkedOn: "2026-09-07",
    recordingOffice: "Clerk of the County Commission",
    recordingFeeNote:
      "Set by statute at W. Va. Code § 59-1-10, which fixes the county clerk's charge for recording writings by page.",
    recordingFeeUrl: "https://code.wvlegislature.gov/59-1-10/",
  },
  {
    stateCode: "WI",
    excerpt:
      "Continuous adverse use of rights in real estate of another for at least 20 years, except as provided in s. 893.29 establishes the prescriptive right to continue the use.",
    sourceSection: "Wis. Stat. § 893.28(1)",
    kind: "statute",
    sourceUrl: "https://docs.legis.wisconsin.gov/statutes/statutes/893/iii/28",
    checkedOn: "2026-09-07",
    recordingOffice: "Register of Deeds",
    recordingFeeNote:
      "Set by statute at Wis. Stat. § 59.43(2): $30 for the first page of most real-estate documents, with additional per-page charges.",
    recordingFeeUrl: "https://docs.legis.wisconsin.gov/document/statutes/59.43(2)",
  },
  {
    stateCode: "WY",
    excerpt: null,
    sourceSection:
      "Wyo. Stat. § 1-3-103 (ten-year period to recover real property), applied to prescriptive easements by the Wyoming Supreme Court (Powder River Ranch, Inc. v. Michelena, 2005 WY 1)",
    kind: "case-law",
    sourceUrl: "https://law.justia.com/codes/wyoming/title-1/chapter-3/section-1-3-103/",
    checkedOn: "2026-09-07",
    recordingOffice: "County Clerk",
    recordingFeeNote:
      "Fee authority sits in Wyo. Stat. §§ 18-3-402 and 34-1-119; county schedules commonly show $12 for the first page and $3 per additional page.",
    recordingFeeUrl: "https://law.justia.com/codes/wyoming/title-18/chapter-3/article-4/section-18-3-402/",
  },
  {
    stateCode: "MT",
    excerpt:
      "In no case shall adverse possessions be considered established under this code unless it shall be shown that the land has been occupied and claimed for a period of 5 years continuously and the party ... have during such period paid all the taxes...",
    sourceSection: "Mont. Code Ann. § 70-19-411",
    kind: "statute",
    sourceUrl:
      "https://mca.legmt.gov/bills/mca/title_0700/chapter_0190/part_0040/section_0110/0700-0190-0040-0110.html",
    checkedOn: "2026-09-07",
    recordingOffice: "County Clerk and Recorder",
    recordingFeeNote:
      "Set by statute at Mont. Code Ann. § 7-4-2637, which fixes the clerk and recorder's charges; confirm the current per-document amount when you file.",
    recordingFeeUrl:
      "https://mca.legmt.gov/bills/mca/title_0070/chapter_0040/part_0260/section_0370/0070-0040-0260-0370.html",
  },
  {
    stateCode: "NE",
    excerpt:
      "An action for the recovery of the title or possession of lands, tenements, or hereditaments, or for the foreclosure of mortgages or the foreclosure of deeds of trust as mortgages thereon, can only be brought within ten years after the cause of action accrues.",
    sourceSection: "Neb. Rev. Stat. § 25-202",
    kind: "statute",
    sourceUrl: "https://nebraskalegislature.gov/laws/statutes.php?statute=25-202",
    checkedOn: "2026-09-07",
    recordingOffice: "Register of Deeds",
    recordingFeeNote:
      "Set by statute at Neb. Rev. Stat. § 33-109, the register of deeds fee schedule.",
    recordingFeeUrl: "https://nebraskalegislature.gov/laws/statutes.php?statute=33-109",
  },
  {
    stateCode: "NV",
    excerpt: null,
    sourceSection: "Nev. Rev. Stat. § 11.150 (five-year adverse possession under claim of title)",
    kind: "statute",
    sourceUrl: "https://www.leg.state.nv.us/nrs/nrs-011.html",
    checkedOn: "2026-09-07",
    recordingOffice: "County Recorder",
    recordingFeeNote: "Set by statute at NRS 247.305, the county recorder's fee schedule.",
    recordingFeeUrl: "https://www.leg.state.nv.us/nrs/nrs-247.html",
  },
  {
    stateCode: "NH",
    excerpt:
      "No action for the recovery of real estate shall be brought after 20 years from the time the right to recover first accrued to the party claiming it or to some persons under whom the party claims.",
    sourceSection: "N.H. Rev. Stat. Ann. § 508:2, I",
    kind: "statute",
    sourceUrl: "https://gc.nh.gov/rsa/html/LII/508/508-2.htm",
    checkedOn: "2026-09-07",
    recordingOffice: "Register of Deeds",
    recordingFeeNote:
      "Set by statute at RSA 478:17-g, the registry of deeds fee schedule.",
    recordingFeeUrl: "https://gc.nh.gov/rsa/html/L/478/478-mrg.htm",
  },
  {
    stateCode: "NJ",
    excerpt: null,
    sourceSection:
      "N.J. Stat. Ann. § 2A:14-6 (twenty-year right of entry) and § 2A:14-31 (twenty-year prescriptive use)",
    kind: "statute",
    sourceUrl: "https://law.justia.com/codes/new-jersey/title-2a/section-2a-14-6/",
    checkedOn: "2026-09-07",
    recordingOffice:
      "County Clerk, or the Register of Deeds and Mortgages in Essex and Hudson counties",
    recordingFeeNote: "Set by statute at N.J.S.A. 22A:4-4.1, the recording fee schedule.",
    recordingFeeUrl: "https://law.justia.com/codes/new-jersey/title-22a/section-22a-4-4.1/",
  },
  {
    stateCode: "NM",
    excerpt: null,
    sourceSection:
      "N.M. Stat. Ann. § 37-1-22 (title in fee simple by adverse possession; action barred after ten years)",
    kind: "statute",
    sourceUrl: "https://law.justia.com/codes/new-mexico/chapter-37/article-1/section-37-1-22/",
    checkedOn: "2026-09-07",
    recordingOffice: "County Clerk",
    recordingFeeNote: "Set by statute at N.M. Stat. Ann. § 14-8-10, the recording fee schedule.",
    recordingFeeUrl: "https://law.justia.com/codes/new-mexico/chapter-14/article-8/section-14-8-10/",
  },
  {
    stateCode: "NY",
    excerpt: null,
    sourceSection:
      "N.Y. Real Prop. Acts. Law § 501 (adverse possession defined), with the ten-year period under RPAPL Article 5 and CPLR 212",
    kind: "statute",
    sourceUrl: "https://www.nysenate.gov/legislation/laws/RPA/501",
    checkedOn: "2026-09-07",
    recordingOffice: "County Clerk, or the City Register in New York City",
    recordingFeeNote:
      "Set by statute at N.Y. Real Property Law § 8021, the recording fee schedule.",
    recordingFeeUrl: "https://www.nysenate.gov/legislation/laws/RPP/8021",
  },
  {
    stateCode: "NC",
    excerpt:
      "No action for the recovery or possession of real property...shall be maintained when the person in possession thereof...has possessed the property under known and visible lines and boundaries adversely to all other persons for 20 years.",
    sourceSection: "N.C. Gen. Stat. § 1-40",
    kind: "statute",
    sourceUrl:
      "https://www.ncleg.gov/EnactedLegislation/Statutes/PDF/BySection/Chapter_1/GS_1-40.pdf",
    checkedOn: "2026-09-07",
    recordingOffice: "Register of Deeds",
    recordingFeeNote:
      "Set by statute at N.C. Gen. Stat. § 161-10, the register of deeds fee schedule.",
    recordingFeeUrl:
      "https://www.ncleg.gov/EnactedLegislation/Statutes/PDF/BySection/Chapter_161/GS_161-10.pdf",
  },
  {
    stateCode: "ND",
    excerpt:
      "A title to real property...vested in any person...in the actual open adverse and undisputed possession of the land under such title for a period of ten years and who...shall have paid all taxes and assessments legally levied thereon, shall be valid in law.",
    sourceSection: "N.D. Cent. Code § 47-06-03",
    kind: "statute",
    sourceUrl: "https://ndlegis.gov/cencode/t47c06.pdf",
    checkedOn: "2026-09-07",
    recordingOffice: "County Recorder",
    recordingFeeNote:
      "Set by statute at N.D. Cent. Code § 11-18-05, the county recorder's fee schedule.",
    recordingFeeUrl: "https://ndlegis.gov/cencode/t11c18.pdf",
  },
  {
    stateCode: "OH",
    excerpt: null,
    sourceSection: "Ohio Rev. Code § 2305.04 (twenty-one years to recover real estate)",
    kind: "statute",
    sourceUrl: "https://codes.ohio.gov/ohio-revised-code/section-2305.04",
    checkedOn: "2026-09-07",
    recordingOffice: "County Recorder",
    recordingFeeNote:
      "Set by statute at Ohio Rev. Code § 317.32, the county recorder's fee schedule.",
    recordingFeeUrl: "https://codes.ohio.gov/ohio-revised-code/section-317.32",
  },
  {
    stateCode: "OK",
    excerpt: null,
    sourceSection: "Okla. Stat. tit. 12, § 93 (fifteen-year limitation on real actions)",
    kind: "statute",
    sourceUrl: "https://law.justia.com/codes/oklahoma/title-12/section-12-93/",
    checkedOn: "2026-09-07",
    recordingOffice: "County Clerk",
    recordingFeeNote: "Set by statute at Okla. Stat. tit. 28, § 32, the county clerk fee schedule.",
    recordingFeeUrl: "https://law.justia.com/codes/oklahoma/title-28/section-28-32/",
  },
  {
    stateCode: "OR",
    excerpt: null,
    sourceSection: "Or. Rev. Stat. § 12.050 (ten years to bring an action to recover real property)",
    kind: "statute",
    sourceUrl: "https://www.oregonlegislature.gov/bills_laws/ors/ors012.html",
    checkedOn: "2026-09-07",
    recordingOffice: "County Clerk, acting as recording officer",
    recordingFeeNote:
      "Oregon sets recording charges county by county under Or. Rev. Stat. § 205.320; first-page charges commonly land in the $90 to $125 range, so check your county's published schedule.",
    recordingFeeUrl: "https://www.oregonlegislature.gov/bills_laws/ors/ors205.html",
  },
  {
    stateCode: "PA",
    excerpt:
      "The following actions and proceedings must be commenced within 21 years: ... an action for the possession of real property.",
    sourceSection: "42 Pa. Cons. Stat. § 5530(a)(1)",
    kind: "statute",
    sourceUrl: "https://www.legis.state.pa.us/WU01/LI/LI/CT/HTM/42/00.055.030.000..HTM",
    checkedOn: "2026-09-07",
    recordingOffice: "Recorder of Deeds",
    recordingFeeNote:
      "Pennsylvania recorders set their own schedules under statutory authority, so the charge varies by county; check the county recorder's published fee list.",
    recordingFeeUrl: "https://www.legis.state.pa.us/",
  },
];

const BY_CODE = new Map(STATE_STATUTORY_EXCERPTS.map((entry) => [entry.stateCode, entry]));

export function statutoryExcerptFor(stateCode: string): StateStatutoryExcerpt | undefined {
  return BY_CODE.get(stateCode.toUpperCase());
}

/** States where the operative language is verified and quotable. */
export function verifiedExcerptCount(): number {
  return STATE_STATUTORY_EXCERPTS.filter((entry) => entry.excerpt !== null).length;
}
